import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';

/**
 * Reply rules — "when somebody does this on WhatsApp, do that".
 *
 * Backend: core-service `/api/v1/whatsapp/reply-rules`.
 *
 * Nothing here is about "interest". That is one RULE, not the shape of the
 * feature: "Interested → ring them" and "Reschedule → enrol them in the
 * follow-up campaign" are the same mechanism twice.
 *
 * A reply already knows which campaign it answers — Meta sends the id of the
 * message the person acted on, and the inbound row inherits its campaign — so
 * scoping a rule to a campaign needs nothing from the operator beyond picking it.
 */

/** What about the inbound message decides a rule applies. */
export type ReplyMatch =
  /** An interactive button's id, or a template button's payload. The stable one. */
  | 'BUTTON_PAYLOAD'
  /** The visible label. Only for templates whose buttons carry no payload. */
  | 'BUTTON_TEXT'
  | 'LIST_ID'
  /** A whole word in a typed reply. */
  | 'KEYWORD'
  /** Anything at all, as long as it is attributed to this rule's campaign. */
  | 'ANY_REPLY'
  /**
   * A lead was captured — from a web form, or recovered after a call.
   *
   * The one match that is not about an inbound message. matchValue names the
   * capture ORIGIN it applies to: WEB, IN_CALL, POST_CALL, or several. Blank
   * means any, which fires on the leads campaign calls produce — so a follow-up
   * begets a follow-up.
   */
  | 'LEAD_CAPTURED';

/**
 * Mirrors ReplyRule.Action on the backend.
 *
 * OPT_OUT writes a permanent do-not-contact entry across every channel — an
 * operator wiring "when they say this, opt them out", in a screen whose other
 * two actions ring people, means stop contacting them. "STOP" itself needs no
 * rule: it is handled before any rule runs.
 */
export type ReplyAction =
  | 'AI_CALL'
  | 'ENROL_CAMPAIGN'
  /** One approved template. Free text is not an option outside WhatsApp's 24-hour window. */
  | 'SEND_WHATSAPP'
  /** A template, or a mail written from what the lead told us. */
  | 'SEND_EMAIL'
  | 'OPT_OUT';

export interface ReplyRule {
  id: string;
  organizationId?: string;
  name: string;
  enabled: boolean;
  /** Null means any campaign — and it is the only kind that sees an unprompted reply. */
  campaignId: string | null;
  matchType: ReplyMatch;
  /** Null only for ANY_REPLY. */
  matchValue: string | null;
  action: ReplyAction;
  /** AI_CALL: the agent that does the talking. */
  agentId: string | null;
  /** SEND_WHATSAPP / SEND_EMAIL: which template to send. */
  templateId?: string | null;
  /**
   * SEND_EMAIL only: what the mail should say, when it is written rather than
   * templated. Naming both means the template is the fallback if the model is
   * unavailable — an outage costs a better mail, not the mail.
   */
  composePrompt?: string | null;
  /** The caller ID to present. Null lets admission choose a presentable one. */
  fromNumber: string | null;
  /** ENROL_CAMPAIGN: routed by engine, so it works on cpass or voicex. */
  voiceCampaignId: string | null;
  /**
   * What to call a caller from THIS rule whose name we do not have.
   * Null defers to the agent's own setting.
   */
  callerNameFallback: string | null;
  /** On by default. A quick-reply button is trivial to tap three times. */
  oncePerContact: boolean;
  createdAt?: string;
  updatedAt?: string | null;
}

/**
 * One firing of a rule.
 *
 * SKIPPED and FAILED are shown alongside DONE on purpose: a view that listed
 * only the calls that happened would make "we called everybody who asked"
 * impossible to disprove.
 */
export interface ReplyRuleAction {
  id: string;
  ruleId: string;
  inboundMessageId: string;
  contactPhone: string;
  campaignId: string | null;
  /** PENDING is either 'not acted on yet' or 'waiting for the calling window'. */
  status: 'PENDING' | 'DONE' | 'SKIPPED' | 'FAILED';
  /** The call id, the enrolment, or why it did not happen. */
  detail: string | null;
  attempts: number;
  nextAttemptAt: string | null;
  createdAt: string;
  actedAt: string | null;
}

export const replyRulesService = {
  list: async (): Promise<ApiResponse<ReplyRule[]>> => {
    try { return (await axiosInstance.get('/v1/whatsapp/reply-rules')).data; }
    catch (e: any) { return apiError(e, 'Failed to load the reply rules'); }
  },

  create: async (body: Partial<ReplyRule>): Promise<ApiResponse<ReplyRule>> => {
    try { return (await axiosInstance.post('/v1/whatsapp/reply-rules', body)).data; }
    catch (e: any) { return apiError(e, 'Failed to create the rule'); }
  },

  update: async (id: string, body: Partial<ReplyRule>): Promise<ApiResponse<ReplyRule>> => {
    try { return (await axiosInstance.put(`/v1/whatsapp/reply-rules/${id}`, body)).data; }
    catch (e: any) { return apiError(e, 'Failed to save the rule'); }
  },

  remove: async (id: string): Promise<ApiResponse<{ deleted: boolean }>> => {
    try { return (await axiosInstance.delete(`/v1/whatsapp/reply-rules/${id}`)).data; }
    catch (e: any) { return apiError(e, 'Failed to delete the rule'); }
  },

  actions: async (limit = 50): Promise<ApiResponse<ReplyRuleAction[]>> => {
    try {
      return (await axiosInstance.get('/v1/whatsapp/reply-rules/actions', { params: { limit } })).data;
    } catch (e: any) { return apiError(e, 'Failed to load what the rules did'); }
  },
};
